import React, { useState } from 'react';
import ImageCompressor from '../../utils/image-compression';
import VideoCompressor from '../../utils/video-compression';

// Define common options for checkboxes
const LANGUAGES_OPTIONS = [
  'English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese',
  'Russian', 'Chinese (Mandarin)', 'Chinese (Cantonese)', 'Japanese',
  'Korean', 'Arabic', 'Hindi', 'Tagalog', 'Vietnamese', 'Polish',
  'Dutch', 'Hebrew', 'Greek', 'Turkish', 'Persian/Farsi', 'Swedish',
  'Norwegian', 'Danish', 'Finnish'
];

const YEARS_EXPERIENCE_OPTIONS = [
  '0-2 years',
  '3-5 years', 
  '6-10 years',
  '11-15 years',
  '16-20 years',
  '20+ years'
];

function ProfileInformationForm({ 
  formData, 
  setFormData, 
  isEditing, 
  validationErrors = {},
  setValidationErrors = () => {},
  error,
  setError,
  success,
  setSuccess,
  onAutoSave
}) {
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [isUploadingGallery, setIsUploadingGallery] = useState(false);

  // Validation helper components
  const RequiredIndicator = () => (
    <span className="text-red-500 font-medium ml-1" title="Required field">*</span>
  );

  const ValidationError = ({ error }) => {
    if (!error) return null;
    return (
      <div className="mt-1 flex items-center gap-1">
        <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-red-600 text-sm font-medium">{error}</p>
      </div>
    );
  };

  const FieldDescription = ({ children }) => (
    <p className="text-xs text-[#254e70]/60 mb-2">{children}</p>
  );

  const CharacterCounter = ({ current, minimum, field }) => {
    const isValid = current >= minimum;
    return (
      <div className="flex justify-between items-center mt-1">
        {validationErrors[field] && (
          <ValidationError error={validationErrors[field]} />
        )}
        <span className={`text-xs ml-auto ${
          isValid ? 'text-gray-500' : 'text-red-500 font-medium'
        }`}>
          {current}/{minimum} characters {!isValid && '(minimum required)'}
        </span>
      </div>
    );
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Clear specific validation error when user starts typing
    if (validationErrors[name.split('.').pop()]) {
      setValidationErrors(prev => ({
        ...prev,
        [name.split('.').pop()]: ''
      }));
    }
  };

  // Helper function to handle nested checkbox arrays
  const handleNestedCheckboxArrayChange = (parent, field, value, checked) => {
    setFormData(prev => {
      const currentArray = prev[parent][field] || [];
      if (checked) {
        return {
          ...prev,
          [parent]: {
            ...prev[parent],
            [field]: [...currentArray, value]
          }
        };
      } else {
        return {
          ...prev,
          [parent]: {
            ...prev[parent],
            [field]: currentArray.filter(item => item !== value)
          }
        };
      }
    });

    // Clear validation error
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleImageUpload = async (file) => {
    setIsUploadingImage(true);
    setError('');
    
    try {
      let fileToUpload = file;
      
      // Compress image if it's larger than 1MB
      if (ImageCompressor.shouldCompress(file)) {
        setSuccess('Compressing image for faster upload...');
        
        const compressedFile = await ImageCompressor.compressImage(file, {
          maxWidth: 1600,
          maxHeight: 1200,
          quality: 0.8,
          format: file.type === 'image/png' ? 'image/png' : 'image/jpeg'
        });
        
        const stats = ImageCompressor.getCompressionStats(file, compressedFile);
        console.log('Compression stats:', stats);
        
        setSuccess(`Image compressed! Saved ${stats.compressionRatio} (${stats.originalSizeMB}MB → ${stats.compressedSizeMB}MB)`);
        fileToUpload = compressedFile;
      }
      
      const token = localStorage.getItem('therapylist_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      
      const uploadFormData = new FormData();
      uploadFormData.append('photo', fileToUpload);
      
      const response = await fetch(`${API_BASE_URL}/api/upload/profile-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload image');
      }
      
      console.log('✅ Image upload successful, updating form data...');
      console.log('📸 New image URL:', data.imageUrl);
      console.log('📸 Current image URL:', formData.profilePhoto);
      
      // Store clean URL and add timestamp to force React re-render via key
      const cleanImageUrl = data.imageUrl.split('?')[0]; // Remove any existing query params
      const imageKey = `${cleanImageUrl}?uploaded=${Date.now()}`;
      
      setFormData(prev => ({
        ...prev,
        profilePhoto: cleanImageUrl,
        profilePhotoKey: imageKey // Store key separately for React re-rendering
      }));
      
      console.log('📸 Updated image URL (clean):', cleanImageUrl);
      console.log('📸 Image key for re-render:', imageKey);
      
      setSuccess('Profile photo uploaded successfully!');
      
      // Auto-save after successful upload
      if (onAutoSave) {
        console.log('💾 Auto-saving profile after photo upload...');
        try {
          await onAutoSave();
          console.log('✅ Profile auto-saved successfully');
        } catch (autoSaveError) {
          console.error('❌ Auto-save failed:', autoSaveError);
          setError('Photo uploaded but failed to save profile. Please save manually.');
        }
      }
      
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Image upload error:', error);
      setError(error.message || 'Failed to upload image');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleVideoUpload = async (file) => {
    setIsUploadingVideo(true);
    setError('');
    
    try {
      console.log('🎥 Starting video upload...', file.name, file.type, file.size);
      
      // Basic validation first (fallback to simple checks)
      const maxSize = 25 * 1024 * 1024; // 25MB limit
      if (file.size > maxSize) {
        throw new Error(
          `Video file is ${Math.round(file.size / (1024 * 1024))}MB. Please compress to under 25MB first. ` +
          `Most phones and computers have built-in compression options, or you can use online compression tools.`
        );
      }

      // Validate file type
      const allowedTypes = ['video/mp4', 'video/mov', 'video/mpeg', 'video/quicktime', 'video/webm'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Please upload a video in MP4, MOV, or WebM format.');
      }

      let fileToUpload = file;
      let videoSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      
      // Try to get video info (optional - if it fails, we'll just continue)
      try {
        console.log('🔍 Getting video info...');
        const videoInfo = await VideoCompressor.getVideoInfo(file);
        console.log('📊 Video info:', videoInfo);
        videoSizeMB = videoInfo.sizeMB;
        setSuccess(`Video: ${videoInfo.width}x${videoInfo.height}, ${videoInfo.duration.toFixed(1)}s, ${videoInfo.sizeMB}MB`);
      } catch (infoError) {
        console.warn('⚠️ Could not get video info:', infoError);
        setSuccess(`Preparing video upload (${videoSizeMB}MB)...`);
      }
      
      console.log('📤 Starting upload to server...');
      const token = localStorage.getItem('therapylist_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      
      const uploadFormData = new FormData();
      uploadFormData.append('video', fileToUpload);
      
      const response = await fetch(`${API_BASE_URL}/api/upload/profile-video`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });
      
      console.log('📨 Upload response status:', response.status);
      
      const data = await response.json();
      console.log('📨 Upload response data:', data);
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload video');
      }
      
      console.log('✅ Upload successful, updating form data...');
      console.log('📺 New video URL:', data.videoUrl);
      console.log('📺 Current video URL:', formData.profileVideo);
      
      // Store clean URL and add timestamp to force React re-render via key
      const cleanVideoUrl = data.videoUrl.split('?')[0]; // Remove any existing query params
      const videoKey = `${cleanVideoUrl}?uploaded=${Date.now()}`;
      
      setFormData(prev => ({
        ...prev,
        profileVideo: cleanVideoUrl,
        profileVideoKey: videoKey // Store key separately for React re-rendering
      }));
      
      console.log('📺 Updated video URL (clean):', cleanVideoUrl);
      console.log('📺 Video key for re-render:', videoKey);
      
      setSuccess(`Profile video uploaded successfully! (${videoSizeMB}MB)`);
      
      // Auto-save after successful upload
      if (onAutoSave) {
        console.log('💾 Auto-saving profile after video upload...');
        try {
          await onAutoSave();
          console.log('✅ Profile auto-saved successfully');
        } catch (autoSaveError) {
          console.error('❌ Auto-save failed:', autoSaveError);
          setError('Video uploaded but failed to save profile. Please save manually.');
        }
      }
      
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('❌ Video upload error:', error);
      setError(error.message || 'Failed to upload video');
    } finally {
      setIsUploadingVideo(false);
    }
  };

  const handleGalleryUpload = async (files) => {
    setIsUploadingGallery(true);
    setError('');
    
    try {
      const filesArray = Array.from(files);
      let totalSaved = 0;
      let processedFiles = [];
      
      // Compress each image if needed
      setSuccess(`Processing ${filesArray.length} images...`);
      
      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i];
        let fileToUpload = file;
        
        if (ImageCompressor.shouldCompress(file)) {
                     const compressedFile = await ImageCompressor.compressImage(file, {
             maxWidth: 1600,
             maxHeight: 1200,
             quality: 0.8,
             format: file.type === 'image/png' ? 'image/png' : 'image/jpeg'
           });
          
          const stats = ImageCompressor.getCompressionStats(file, compressedFile);
          totalSaved += stats.savedBytes;
          fileToUpload = compressedFile;
          
          setSuccess(`Compressing image ${i + 1}/${filesArray.length}...`);
        }
        
        processedFiles.push(fileToUpload);
      }
      
      if (totalSaved > 0) {
        const totalSavedMB = (totalSaved / (1024 * 1024)).toFixed(2);
        setSuccess(`Compression complete! Saved ${totalSavedMB}MB total. Uploading...`);
      } else {
        setSuccess('Uploading images...');
      }
      
      const token = localStorage.getItem('therapylist_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      
      const uploadFormData = new FormData();
      processedFiles.forEach(file => {
        uploadFormData.append('photos', file);
      });
      
      const response = await fetch(`${API_BASE_URL}/api/upload/gallery`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload gallery photos');
      }
      
      // Add new images to existing gallery
      setFormData(prev => ({
        ...prev,
        gallery: [...prev.gallery, ...data.images.map(img => img.url)]
      }));
      
      const savedMessage = totalSaved > 0 ? ` (saved ${(totalSaved / (1024 * 1024)).toFixed(2)}MB)` : '';
      setSuccess(`${data.images.length} photos uploaded successfully to gallery!${savedMessage}`);
      
      // Auto-save after successful gallery upload
      if (onAutoSave) {
        console.log('💾 Auto-saving profile after gallery upload...');
        try {
          await onAutoSave();
          console.log('✅ Profile auto-saved successfully');
        } catch (autoSaveError) {
          console.error('❌ Auto-save failed:', autoSaveError);
          setError('Gallery photos uploaded but failed to save profile. Please save manually.');
        }
      }
      
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Gallery upload error:', error);
      setError(error.message || 'Failed to upload gallery photos');
    } finally {
      setIsUploadingGallery(false);
    }
  };

  // Helper function to extract filename from Vercel blob URL
  const extractFilenameFromUrl = (url) => {
    try {
      // Remove timestamp parameter if present
      const cleanUrl = url.split('?')[0];
      // Extract the path after the domain
      const urlParts = cleanUrl.split('/');
      const pathIndex = urlParts.findIndex(part => part === 'therapylist');
      if (pathIndex !== -1) {
        return urlParts.slice(pathIndex).join('/');
      }
      return null;
    } catch (error) {
      console.error('Error extracting filename:', error);
      return null;
    }
  };

  // Delete file from Vercel blob storage
  const deleteFileFromStorage = async (fileUrl, fileType = 'file') => {
    try {
      const filename = extractFilenameFromUrl(fileUrl);
      if (!filename) {
        throw new Error('Could not extract filename from URL');
      }

      console.log(`🗑️ Deleting ${fileType}:`, filename);

      const token = localStorage.getItem('therapylist_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      const requestBody = { filename };
      console.log('📤 Delete request body:', requestBody);
      console.log('📤 Delete request URL:', `${API_BASE_URL}/api/upload/file`);

      const response = await fetch(`${API_BASE_URL}/api/upload/file`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      console.log('📨 Delete response status:', response.status);

      const data = await response.json();
      console.log('📨 Delete response data:', data);

      if (!response.ok) {
        throw new Error(data.message || `Failed to delete ${fileType}`);
      }

      console.log(`✅ ${fileType} deleted successfully from storage`);
      return true;
    } catch (error) {
      console.error(`❌ Error deleting ${fileType}:`, error);
      setError(`Failed to delete ${fileType}: ${error.message}`);
      return false;
    }
  };

  const handleProfilePhotoDelete = async () => {
    if (!formData.profilePhoto) return;
    
    const confirmed = window.confirm('Are you sure you want to delete your profile photo?');
    if (!confirmed) return;

    setIsUploadingImage(true);
    setError('');

    try {
      const deleted = await deleteFileFromStorage(formData.profilePhoto, 'profile photo');
      if (deleted) {
        setFormData(prev => ({
          ...prev,
          profilePhoto: '',
          profilePhotoKey: `deleted-${Date.now()}` // Force re-render after deletion
        }));
        setSuccess('Profile photo deleted successfully');
        
        // Auto-save after successful deletion
        if (onAutoSave) {
          console.log('💾 Auto-saving profile after photo deletion...');
          try {
            await onAutoSave();
            console.log('✅ Profile auto-saved successfully');
          } catch (autoSaveError) {
            console.error('❌ Auto-save failed:', autoSaveError);
            setError('Photo deleted but failed to save profile. Please save manually.');
          }
        }
        
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Profile photo delete error:', error);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleProfileVideoDelete = async () => {
    if (!formData.profileVideo) return;
    
    const confirmed = window.confirm('Are you sure you want to delete your introduction video?');
    if (!confirmed) return;

    setIsUploadingVideo(true);
    setError('');

    try {
      const deleted = await deleteFileFromStorage(formData.profileVideo, 'profile video');
      if (deleted) {
        setFormData(prev => ({
          ...prev,
          profileVideo: '',
          profileVideoKey: `deleted-${Date.now()}` // Force re-render after deletion
        }));
        setSuccess('Profile video deleted successfully');
        
        // Auto-save after successful deletion
        if (onAutoSave) {
          console.log('💾 Auto-saving profile after video deletion...');
          try {
            await onAutoSave();
            console.log('✅ Profile auto-saved successfully');
          } catch (autoSaveError) {
            console.error('❌ Auto-save failed:', autoSaveError);
            setError('Video deleted but failed to save profile. Please save manually.');
          }
        }
        
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Profile video delete error:', error);
    } finally {
      setIsUploadingVideo(false);
    }
  };

  const handleGalleryImageRemove = async (imageUrl) => {
    const confirmed = window.confirm('Are you sure you want to delete this photo from your gallery?');
    if (!confirmed) return;

    setError('');

    try {
      const deleted = await deleteFileFromStorage(imageUrl, 'gallery photo');
      if (deleted) {
        setFormData(prev => ({
          ...prev,
          gallery: prev.gallery.filter(url => url !== imageUrl)
        }));
        setSuccess('Photo deleted from gallery');
        
        // Auto-save after successful deletion
        if (onAutoSave) {
          console.log('💾 Auto-saving profile after gallery photo deletion...');
          try {
            await onAutoSave();
            console.log('✅ Profile auto-saved successfully');
          } catch (autoSaveError) {
            console.error('❌ Auto-save failed:', autoSaveError);
            setError('Gallery photo deleted but failed to save profile. Please save manually.');
          }
        }
        
        setTimeout(() => setSuccess(''), 2000);
      }
    } catch (error) {
      console.error('Gallery photo delete error:', error);
    }
  };

  const handleWebsiteVisit = (websiteUrl) => {
    if (!websiteUrl) return;

    // Ensure URL has protocol
    let formattedUrl = websiteUrl;
    if (!websiteUrl.startsWith('http://') && !websiteUrl.startsWith('https://')) {
      formattedUrl = `https://${websiteUrl}`;
    }

    try {
      // Open in new tab
      window.open(formattedUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Invalid website URL:', error);
      // Fallback: try opening the original URL
      window.open(websiteUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="space-y-4">
      {/* Basic Information */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-[#254e70]/10">
        <h3 className="text-base font-semibold text-[#254e70] mb-3" style={{ fontFamily: 'Kanit, sans-serif' }}>
          Basic Information
          <RequiredIndicator />
        </h3>
        <FieldDescription>
          Provide your basic professional information that clients will see on your profile
        </FieldDescription>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#254e70] mb-1">
                First Name
                <RequiredIndicator />
              </label>
              {isEditing ? (
                <div>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] ${
                      validationErrors.firstName ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                    }`}
                    placeholder="Your first name"
                    required
                  />
                  <ValidationError error={validationErrors.firstName} />
                </div>
              ) : (
                <p className="py-2 text-[#254e70]/80">{formData.firstName || 'Not set'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#254e70] mb-1">
                Last Name
                <RequiredIndicator />
              </label>
              {isEditing ? (
                <div>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] ${
                      validationErrors.lastName ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                    }`}
                    placeholder="Your last name"
                    required
                  />
                  <ValidationError error={validationErrors.lastName} />
                </div>
              ) : (
                <p className="py-2 text-[#254e70]/80">{formData.lastName || 'Not set'}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-1">
              Professional Type
              <RequiredIndicator />
            </label>
            <FieldDescription>
              Select your primary professional category
            </FieldDescription>
            {isEditing ? (
              <div>
                <select
                  name="professionalType"
                  value={formData.professionalType}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] ${
                    validationErrors.professionalType ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                  }`}
                  required
                >
                  <option value="">Select Professional Type</option>
                  <option value="Therapist">Therapist</option>
                  <option value="Psychologist">Psychologist</option>
                  <option value="Psychiatrist">Psychiatrist</option>
                </select>
                <ValidationError error={validationErrors.professionalType} />
              </div>
            ) : (
              <p className="py-2 text-[#254e70]/80">{formData.professionalType || 'Not set'}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#254e70] mb-1">
                Pronouns
                <span className="text-xs text-[#254e70]/60 ml-1">(Optional)</span>
              </label>
              {isEditing ? (
                <select
                  name="pronouns"
                  value={formData.pronouns}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#9dacff]"
                >
                  <option value="">Select Pronouns</option>
                  <option value="She/Her">She/Her</option>
                  <option value="He/Him">He/Him</option>
                  <option value="They/Them">They/Them</option>
                  <option value="She/They">She/They</option>
                  <option value="He/They">He/They</option>
                  <option value="Other">Other</option>
                </select>
              ) : (
                <p className="py-2 text-[#254e70]/80">{formData.pronouns || 'Not specified'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#254e70] mb-1">
                Gender
                <span className="text-xs text-[#254e70]/60 ml-1">(Optional)</span>
              </label>
              {isEditing ? (
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#9dacff]"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Non-binary">Non-binary</option>
                  <option value="Prefer not to say">Prefer not to say</option>
                </select>
              ) : (
                <p className="py-2 text-[#254e70]/80">{formData.gender || 'Not specified'}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#254e70] mb-1">
                Credentials
                <span className="text-xs text-[#254e70]/60 ml-1">(Optional, comma-separated)</span>
              </label>
              <FieldDescription>
                List your professional credentials (e.g., LCSW, PhD, MSW, LMFT)
              </FieldDescription>
              {isEditing ? (
                <div>
                  <input
                    type="text"
                    name="credentials"
                    value={formData.credentialsRaw || formData.credentials.join(', ')}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Store raw text for typing experience
                      setFormData(prev => ({
                        ...prev,
                        credentialsRaw: value,
                        credentials: value
                          .split(',')
                          .map(cred => cred.trim())
                          .filter(cred => cred.length > 0)
                      }));
                      
                      // Clear validation error
                      if (validationErrors.credentials) {
                        setValidationErrors(prev => ({
                          ...prev,
                          credentials: ''
                        }));
                      }
                    }}
                    onBlur={(e) => {
                      // Clean up the raw text on blur
                      const value = e.target.value;
                      const credentialsArray = value
                        .split(',')
                        .map(cred => cred.trim())
                        .filter(cred => cred.length > 0);
                      setFormData(prev => ({
                        ...prev,
                        credentialsRaw: credentialsArray.join(', '),
                        credentials: credentialsArray
                      }));
                    }}
                    placeholder="LCSW, PhD, MSW, LMFT, etc."
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] ${
                      validationErrors.credentials ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                    }`}
                  />
                  <div className="flex justify-between items-start mt-1">
                    <ValidationError error={validationErrors.credentials} />
                    <p className="text-xs text-[#254e70]/60">
                      {formData.credentials.length} credentials
                    </p>
                  </div>
                  {formData.credentials.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {formData.credentials.map((credential, index) => (
                        <span 
                          key={index} 
                          className="inline-block px-2 py-1 bg-[#9dacff]/10 text-[#254e70] rounded text-xs font-medium"
                        >
                          {credential}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="py-2 text-[#254e70]/80">
                  {formData.credentials.length > 0 ? formData.credentials.join(', ') : 'Not set'}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#254e70] mb-1">
                Professional Title
                <span className="text-xs text-[#254e70]/60 ml-1">(Optional)</span>
              </label>
              <FieldDescription>
                A specialty or focus area title (e.g., "Trauma Specialist")
              </FieldDescription>
              {isEditing ? (
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Trauma Specialist"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#9dacff]"
                />
              ) : (
                <p className="py-2 text-[#254e70]/80">{formData.title || 'Not specified'}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#254e70] mb-1">
                Email
                <RequiredIndicator />
              </label>
              <FieldDescription>
                Professional email address for client contact
              </FieldDescription>
              {isEditing ? (
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] ${
                      validationErrors.email ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                    }`}
                    placeholder="your.email@example.com"
                  />
                  <ValidationError error={validationErrors.email} />
                </div>
              ) : (
                <p className="py-2 text-[#254e70]/80">{formData.email || 'Not set'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#254e70] mb-1">
                Phone
                <RequiredIndicator />
              </label>
              <FieldDescription>
                Primary phone number for client contact
              </FieldDescription>
              {isEditing ? (
                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] ${
                      validationErrors.phone ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                    }`}
                    placeholder="(555) 123-4567"
                  />
                  <ValidationError error={validationErrors.phone} />
                </div>
              ) : (
                <p className="py-2 text-[#254e70]/80">{formData.phone || 'Not set'}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-1">
              Practice Name
              <span className="text-xs text-[#254e70]/60 ml-1">(Optional)</span>
            </label>
            <FieldDescription>
              Name of your practice or clinic (if applicable)
            </FieldDescription>
            {isEditing ? (
              <input
                type="text"
                name="practiceName"
                value={formData.practiceName}
                onChange={handleInputChange}
                placeholder="Your Practice Name"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#9dacff]"
              />
            ) : (
              <p className="py-2 text-[#254e70]/80">{formData.practiceName || 'Not specified'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-1">
              Professional Bio
              <RequiredIndicator />
            </label>
            <FieldDescription>
              Tell potential clients about yourself and your approach to therapy (minimum 100 characters)
            </FieldDescription>
            {isEditing ? (
              <div>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  placeholder="Tell potential clients about yourself and your approach to therapy..."
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] h-32 ${
                    validationErrors.bio ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                  }`}
                />
                <CharacterCounter 
                  current={formData.bio.length} 
                  minimum={100} 
                  field="bio"
                />
              </div>
            ) : (
              <p className="py-2 text-[#254e70]/80 whitespace-pre-wrap">{formData.bio || 'Not set'}</p>
            )}
          </div>
        </div>
      </div>

      {/* Professional Information */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-[#254e70]/10">
        <h3 className="text-base font-semibold text-[#254e70] mb-3" style={{ fontFamily: 'Kanit, sans-serif' }}>
          Professional Credentials
          <span className="text-sm font-normal text-[#254e70]/60 ml-2">(Optional)</span>
        </h3>
        <FieldDescription>
          Add your professional licensing and credential information (can be completed later)
        </FieldDescription>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-1">
              NPI Number
              <span className="text-xs text-[#254e70]/60 ml-1">(Optional)</span>
            </label>
            <FieldDescription>
              Your 10-digit National Provider Identifier number (can be added later)
            </FieldDescription>
            {isEditing ? (
              <div>
                <input
                  type="text"
                  name="professional.npiNumber"
                  value={formData.professional.npiNumber}
                  onChange={handleInputChange}
                  placeholder="1234567890"
                  maxLength="10"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] ${
                    validationErrors.npiNumber ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                  }`}
                  required
                />
                <ValidationError error={validationErrors.npiNumber} />
              </div>
            ) : (
              <p className="py-2 text-[#254e70]/80">{formData.professional.npiNumber || 'Not set'}</p>
            )}
          </div>

          {/* License Information */}
          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-2">
              Professional Licenses
              <span className="text-xs text-[#254e70]/60 ml-1">(Optional)</span>
            </label>
            <FieldDescription>
              Add your active professional licenses (can be added or updated later)
            </FieldDescription>
            {isEditing ? (
              <div className="space-y-3">
                {formData.professional.licenses && formData.professional.licenses.length > 0 ? (
                  formData.professional.licenses.map((license, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-[#254e70] mb-1">License Number</label>
                          <input
                            type="text"
                            value={license.number || ''}
                            onChange={(e) => {
                              const newLicenses = [...formData.professional.licenses];
                              newLicenses[index] = { ...newLicenses[index], number: e.target.value };
                              setFormData(prev => ({
                                ...prev,
                                professional: { ...prev.professional, licenses: newLicenses }
                              }));
                            }}
                            className="w-full px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:border-[#9dacff]"
                            placeholder="License number"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-[#254e70] mb-1">State</label>
                          <input
                            type="text"
                            value={license.state || ''}
                            onChange={(e) => {
                              const newLicenses = [...formData.professional.licenses];
                              newLicenses[index] = { ...newLicenses[index], state: e.target.value };
                              setFormData(prev => ({
                                ...prev,
                                professional: { ...prev.professional, licenses: newLicenses }
                              }));
                            }}
                            className="w-full px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:border-[#9dacff]"
                            placeholder="CA, NY, etc."
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div>
                            <label className="block text-xs font-medium text-[#254e70] mb-1">Status</label>
                            <select
                              value={license.status || 'active'}
                              onChange={(e) => {
                                const newLicenses = [...formData.professional.licenses];
                                newLicenses[index] = { ...newLicenses[index], status: e.target.value };
                                setFormData(prev => ({
                                  ...prev,
                                  professional: { ...prev.professional, licenses: newLicenses }
                                }));
                              }}
                              className="px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:border-[#9dacff]"
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                              <option value="suspended">Suspended</option>
                              <option value="expired">Expired</option>
                            </select>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newLicenses = formData.professional.licenses.filter((_, i) => i !== index);
                            setFormData(prev => ({
                              ...prev,
                              professional: { ...prev.professional, licenses: newLicenses }
                            }));
                          }}
                          className="text-red-500 hover:text-red-700 text-sm px-2 py-1 hover:bg-red-50 rounded"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <p className="text-sm text-gray-500 italic">No licenses added yet</p>
                    <p className="text-xs text-gray-400 mt-1">Click "Add License" below to get started</p>
                  </div>
                )}
                
                <button
                  type="button"
                  onClick={() => {
                    const newLicense = {
                      licenseType: '',
                      number: formData.professional.licenseNumber || '',
                      state: '',
                      status: 'active'
                    };
                    const currentLicenses = formData.professional.licenses || [];
                    setFormData(prev => ({
                      ...prev,
                      professional: {
                        ...prev.professional,
                        licenses: [...currentLicenses, newLicense]
                      }
                    }));
                  }}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-[#254e70] hover:border-[#9dacff] hover:bg-[#9dacff]/5 transition-colors text-sm font-medium"
                >
                  + Add License
                </button>
                <ValidationError error={validationErrors.licenses} />
              </div>
            ) : (
              <div>
                {formData.professional.licenses && formData.professional.licenses.length > 0 ? (
                  <div className="space-y-2">
                    {formData.professional.licenses.map((license, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div>
                          <span className="font-medium">{license.number}</span>
                          {license.state && <span className="ml-2 text-[#254e70]/60">({license.state})</span>}
                          <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                            license.status === 'active' ? 'bg-green-100 text-green-800' :
                            license.status === 'expired' ? 'bg-red-100 text-red-800' :
                            license.status === 'suspended' ? 'bg-orange-100 text-orange-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {license.status || 'Active'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="py-2 text-[#254e70]/80">No licenses added</p>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#254e70] mb-1">
                Years of Experience
                <span className="text-xs text-[#254e70]/60 ml-1">(Optional)</span>
              </label>
              <FieldDescription>
                How long have you been practicing professionally?
              </FieldDescription>
              {isEditing ? (
                <div>
                  <select
                    name="professional.yearsExperience"
                    value={formData.professional.yearsExperience}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] ${
                      validationErrors.yearsExperience ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                    }`}
                  >
                    <option value="">Select experience range</option>
                    {YEARS_EXPERIENCE_OPTIONS.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  <ValidationError error={validationErrors.yearsExperience} />
                </div>
              ) : (
                <p className="py-2 text-[#254e70]/80">{formData.professional.yearsExperience || 'Not set'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#254e70] mb-1">
                Languages Spoken
                <span className="text-xs text-[#254e70]/60 ml-1">(Optional)</span>
              </label>
              <FieldDescription>
                Select all languages you can conduct therapy in
              </FieldDescription>
              {isEditing ? (
                <div>
                  <div className={`grid grid-cols-2 gap-1 max-h-32 overflow-y-auto border rounded-lg p-2 ${
                    validationErrors.languages ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                  }`}>
                    {LANGUAGES_OPTIONS.map(language => (
                      <div key={language} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`language-${language}`}
                          checked={formData.professional.languages.includes(language)}
                          onChange={(e) => handleNestedCheckboxArrayChange('professional', 'languages', language, e.target.checked)}
                          className="mr-1 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                        />
                        <label htmlFor={`language-${language}`} className="text-xs text-[#254e70]">
                          {language}
                        </label>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-start mt-1">
                    <ValidationError error={validationErrors.languages} />
                    <p className="text-xs text-[#254e70]/60">
                      {formData.professional.languages.length} selected (minimum 1 required)
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {formData.professional.languages.length > 0 ? 
                    formData.professional.languages.map(lang => (
                      <span key={lang} className="bg-[#e4fde1] text-[#254e70] px-2 py-1 rounded-full text-xs">
                        {lang}
                      </span>
                    )) : 
                    <p className="text-[#254e70]/80">Not set</p>
                  }
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#254e70] mb-1">
                Education
                <span className="text-xs text-[#254e70]/60 ml-1">(Optional)</span>
              </label>
              <FieldDescription>
                Add your relevant education and degrees
              </FieldDescription>
              {isEditing ? (
                <div className="space-y-3">
                  {formData.professional.education && formData.professional.education.length > 0 ? (
                    formData.professional.education.map((education, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <input
                            type="text"
                            value={typeof education === 'string' ? education : education.degree || education}
                            onChange={(e) => {
                              const newEducation = [...formData.professional.education];
                              newEducation[index] = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                professional: { ...prev.professional, education: newEducation }
                              }));
                            }}
                            className="flex-1 px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-[#9dacff]"
                            placeholder="e.g., PhD Psychology - University Name"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newEducation = formData.professional.education.filter((_, i) => i !== index);
                              setFormData(prev => ({
                                ...prev,
                                professional: { ...prev.professional, education: newEducation }
                              }));
                            }}
                            className="ml-2 text-red-500 hover:text-red-700 text-sm px-2 py-1 hover:bg-red-50 rounded"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                      <p className="text-sm text-gray-500 italic">No education entries added yet</p>
                      <p className="text-xs text-gray-400 mt-1">Click "Add Education" below to get started</p>
                    </div>
                  )}
                  
                  <button
                    type="button"
                    onClick={() => {
                      const currentEducation = formData.professional.education || [];
                      setFormData(prev => ({
                        ...prev,
                        professional: {
                          ...prev.professional,
                          education: [...currentEducation, '']
                        }
                      }));
                    }}
                    className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-[#254e70] hover:border-[#9dacff] hover:bg-[#9dacff]/5 transition-colors text-sm font-medium"
                  >
                    + Add Education
                  </button>
                </div>
              ) : (
                <div>
                  {formData.professional.education && formData.professional.education.length > 0 ? (
                    <div className="space-y-1">
                      {formData.professional.education.map((education, index) => (
                        <div key={index} className="p-2 bg-gray-50 rounded text-sm">
                          {typeof education === 'string' ? education : education.degree || education}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="py-2 text-[#254e70]/80">No education entries added</p>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#254e70] mb-1">
                Certifications
                <span className="text-xs text-[#254e70]/60 ml-1">(Optional)</span>
              </label>
              <FieldDescription>
                Add any additional certifications or specialized training
              </FieldDescription>
              {isEditing ? (
                <div className="space-y-3">
                  {formData.professional.certifications && formData.professional.certifications.length > 0 ? (
                    formData.professional.certifications.map((certification, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <input
                            type="text"
                            value={typeof certification === 'string' ? certification : certification.name || certification}
                            onChange={(e) => {
                              const newCertifications = [...formData.professional.certifications];
                              newCertifications[index] = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                professional: { ...prev.professional, certifications: newCertifications }
                              }));
                            }}
                            className="flex-1 px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-[#9dacff]"
                            placeholder="e.g., EMDR Certified Therapist"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newCertifications = formData.professional.certifications.filter((_, i) => i !== index);
                              setFormData(prev => ({
                                ...prev,
                                professional: { ...prev.professional, certifications: newCertifications }
                              }));
                            }}
                            className="ml-2 text-red-500 hover:text-red-700 text-sm px-2 py-1 hover:bg-red-50 rounded"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                      <p className="text-sm text-gray-500 italic">No certifications added yet</p>
                      <p className="text-xs text-gray-400 mt-1">Click "Add Certification" below to get started</p>
                    </div>
                  )}
                  
                  <button
                    type="button"
                    onClick={() => {
                      const currentCertifications = formData.professional.certifications || [];
                      setFormData(prev => ({
                        ...prev,
                        professional: {
                          ...prev.professional,
                          certifications: [...currentCertifications, '']
                        }
                      }));
                    }}
                    className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-[#254e70] hover:border-[#9dacff] hover:bg-[#9dacff]/5 transition-colors text-sm font-medium"
                  >
                    + Add Certification
                  </button>
                </div>
              ) : (
                <div>
                  {formData.professional.certifications && formData.professional.certifications.length > 0 ? (
                    <div className="space-y-1">
                      {formData.professional.certifications.map((certification, index) => (
                        <div key={index} className="p-2 bg-gray-50 rounded text-sm">
                          {typeof certification === 'string' ? certification : certification.name || certification}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="py-2 text-[#254e70]/80">No certifications added</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Profile Media */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-[#254e70]/10">
        <h3 className="text-base font-semibold text-[#254e70] mb-3" style={{ fontFamily: 'Kanit, sans-serif' }}>
          Profile Media
          <span className="text-sm font-normal text-[#254e70]/60 ml-2">(Optional but Recommended)</span>
        </h3>
        <FieldDescription>
          Add photos and videos to help clients connect with you personally
        </FieldDescription>
        <div className="space-y-3">
          {/* Profile Photo */}
          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-2">
              Profile Photo
              <span className="text-xs text-[#254e70]/60 ml-1">(Highly Recommended)</span>
            </label>
            <FieldDescription>
              A professional headshot helps clients feel more comfortable reaching out
            </FieldDescription>
            {formData.profilePhoto && (
              <div className="mb-3 relative inline-block">
                <img 
                  key={formData.profilePhotoKey || formData.profilePhoto}
                  src={formData.profilePhoto} 
                  alt="Profile" 
                  className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                />
                {isEditing && (
                  <button
                    onClick={handleProfilePhotoDelete}
                    disabled={isUploadingImage}
                    className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-colors duration-200 disabled:opacity-50"
                    title="Delete profile photo"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            )}
            {isEditing && (
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleImageUpload(file);
                  }}
                  className="hidden"
                  id="profile-photo-upload"
                  disabled={isUploadingImage}
                />
                <label
                  htmlFor="profile-photo-upload"
                  className={`px-4 py-2 border border-[#254e70]/20 text-[#254e70] rounded-lg hover:bg-[#254e70]/5 transition-colors duration-200 cursor-pointer text-sm ${
                    isUploadingImage ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isUploadingImage ? 'Uploading...' : 'Choose Photo'}
                </label>
                <span className="text-xs text-[#254e70]/70">JPG, PNG up to 5MB (auto-compressed)</span>
              </div>
            )}
            {!isEditing && !formData.profilePhoto && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-700 text-sm">⚠️ No profile photo uploaded - highly recommended for better client engagement</p>
              </div>
            )}
          </div>

          {/* Profile Video */}
          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-2">
              Introduction Video
              <span className="text-xs text-[#254e70]/60 ml-1">(Optional)</span>
            </label>
            <FieldDescription>
              A short introduction video helps clients connect with you personally
            </FieldDescription>
            {formData.profileVideo && (
              <div className="mb-3 relative">
                <video 
                  key={formData.profileVideoKey || formData.profileVideo}
                  src={formData.profileVideo} 
                  controls 
                  className="w-full max-w-md h-48 object-cover rounded-lg border border-gray-200"
                />
                {isEditing && (
                  <button
                    onClick={handleProfileVideoDelete}
                    disabled={isUploadingVideo}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-colors duration-200 disabled:opacity-50"
                    title="Delete profile video"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            )}
            {isEditing && (
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleVideoUpload(file);
                  }}
                  className="hidden"
                  id="profile-video-upload"
                  disabled={isUploadingVideo}
                />
                <label
                  htmlFor="profile-video-upload"
                  className={`px-4 py-2 border border-[#254e70]/20 text-[#254e70] rounded-lg hover:bg-[#254e70]/5 transition-colors duration-200 cursor-pointer text-sm ${
                    isUploadingVideo ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isUploadingVideo ? 'Uploading...' : 'Choose Video'}
                </label>
                <span className="text-xs text-[#254e70]/70">MP4, MOV, WebM up to 25MB</span>
              </div>
            )}
            {!isEditing && !formData.profileVideo && (
              <p className="text-[#254e70]/80 text-sm">No video uploaded</p>
            )}
            <div className="mt-2 space-y-1">
              <p className="text-xs text-[#254e70]/70">
                💡 <strong>Tip:</strong> Keep videos under 2 minutes. Large videos will be automatically compressed when possible.
              </p>
            </div>
          </div>

          {/* Enhanced Photo Gallery */}
          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-2">
              Photo Gallery
              <span className="text-xs text-[#254e70]/60 ml-1">(Optional)</span>
            </label>
            <FieldDescription>
              Showcase your practice environment, office space, or professional photos
            </FieldDescription>
            
            {/* Current Gallery Images */}
            {formData.gallery && formData.gallery.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-[#254e70]/70 mb-2">Current Photos ({formData.gallery.length}/10)</p>
                {isEditing && (
                  <div className="mb-3 space-y-1">
                    <p className="text-xs text-[#254e70]/60">
                      💡 Drag photos to reorder them. The first photo will be featured prominently.
                    </p>
                    <p className="text-xs text-[#254e70]/60">
                      📷 Large images are automatically compressed to save space and improve loading times.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {formData.gallery.map((imageUrl, index) => (
                    <div 
                      key={index} 
                      className={`relative group ${isEditing ? 'cursor-move' : ''}`}
                      draggable={isEditing}
                      onDragStart={(e) => {
                        if (isEditing) {
                          e.dataTransfer.setData('text/plain', index.toString());
                          e.dataTransfer.effectAllowed = 'move';
                        }
                      }}
                      onDragOver={(e) => {
                        if (isEditing) {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                          e.currentTarget.classList.add('ring-2', 'ring-[#9dacff]');
                        }
                      }}
                      onDragLeave={(e) => {
                        if (isEditing) {
                          e.currentTarget.classList.remove('ring-2', 'ring-[#9dacff]');
                        }
                      }}
                      onDrop={(e) => {
                        if (isEditing) {
                          e.preventDefault();
                          e.currentTarget.classList.remove('ring-2', 'ring-[#9dacff]');
                          const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
                          const dropIndex = index;
                          
                          if (dragIndex !== dropIndex) {
                            const newGallery = [...formData.gallery];
                            const draggedItem = newGallery[dragIndex];
                            newGallery.splice(dragIndex, 1);
                            newGallery.splice(dropIndex, 0, draggedItem);
                            
                            setFormData(prev => ({
                              ...prev,
                              gallery: newGallery
                            }));
                          }
                        }
                      }}
                    >
                      <div className="relative">
                        <img 
                          src={imageUrl} 
                          alt={`Gallery ${index + 1}`} 
                          className="w-full h-32 object-cover rounded-lg border border-gray-200 transition-transform duration-200 hover:scale-105"
                          onError={(e) => {
                            // Handle broken image URLs
                            e.target.style.display = 'none';
                            const errorDiv = document.createElement('div');
                            errorDiv.className = 'w-full h-32 bg-red-50 border border-red-200 rounded-lg flex items-center justify-center text-red-500 text-xs';
                            errorDiv.innerHTML = '❌ Broken image';
                            e.target.parentNode.appendChild(errorDiv);
                          }}
                        />
                        {index === 0 && (
                          <div className="absolute bottom-1 left-1 bg-[#9dacff] text-white text-xs px-2 py-1 rounded">
                            Featured
                          </div>
                        )}
                      </div>
                      
                      {isEditing && (
                        <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <button
                            onClick={() => {
                              // Move to front (make featured)
                              if (index !== 0) {
                                const newGallery = [...formData.gallery];
                                const movedItem = newGallery.splice(index, 1)[0];
                                newGallery.unshift(movedItem);
                                setFormData(prev => ({
                                  ...prev,
                                  gallery: newGallery
                                }));
                              }
                            }}
                            className="bg-[#9dacff] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-[#7a8cdd] transition-colors"
                            title="Make featured photo"
                          >
                            ⭐
                          </button>
                          <button
                            onClick={() => handleGalleryImageRemove(imageUrl)}
                            className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                            title="Remove image"
                          >
                            ×
                          </button>
                        </div>
                      )}
                      
                      {isEditing && (
                        <div className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                          {index + 1}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isEditing && (
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = e.target.files;
                      if (files && files.length > 0) {
                        // Check if adding these files would exceed the limit
                        const currentCount = formData.gallery.length;
                        const newCount = currentCount + files.length;
                        if (newCount > 10) {
                          setError(`Gallery can only have 10 photos maximum. You currently have ${currentCount} photos.`);
                          return;
                        }
                        handleGalleryUpload(files);
                      }
                    }}
                    className="hidden"
                    id="gallery-upload"
                    disabled={isUploadingGallery || formData.gallery.length >= 10}
                  />
                  <label
                    htmlFor="gallery-upload"
                    className={`px-4 py-2 border border-[#254e70]/20 text-[#254e70] rounded-lg hover:bg-[#254e70]/5 transition-colors duration-200 cursor-pointer text-sm ${
                      isUploadingGallery || formData.gallery.length >= 10 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isUploadingGallery ? 'Uploading...' : 'Add Photos to Gallery'}
                  </label>
                  <span className="text-xs text-[#254e70]/70">
                    JPG, PNG up to 10MB each (max 10 photos)
                  </span>
                </div>
                
                {formData.gallery.length >= 10 && (
                  <p className="text-amber-600 text-xs">
                    ⚠️ Gallery is full (10/10 photos). Remove some photos to add new ones.
                  </p>
                )}
                
                <div 
                  className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-4 transition-colors duration-200 hover:bg-gray-100"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('bg-blue-50', 'border-blue-300');
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('bg-blue-50', 'border-blue-300');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('bg-blue-50', 'border-blue-300');
                    
                    const files = Array.from(e.dataTransfer.files).filter(file => 
                      file.type.startsWith('image/')
                    );
                    
                    if (files.length > 0) {
                      const currentCount = formData.gallery.length;
                      const newCount = currentCount + files.length;
                      if (newCount > 10) {
                        setError(`Gallery can only have 10 photos maximum. You currently have ${currentCount} photos.`);
                        return;
                      }
                      handleGalleryUpload(files);
                    }
                  }}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">📷</div>
                    <p className="text-sm text-gray-600 mb-1">
                      Drag and drop photos here or click "Add Photos to Gallery"
                    </p>
                    <p className="text-xs text-gray-500">
                      Showcase your practice environment, office space, or professional photos
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {!isEditing && formData.gallery.length === 0 && (
              <p className="text-[#254e70]/80 text-sm">No gallery photos uploaded</p>
            )}
          </div>

          {/* Website */}
          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-1">
              Website
              <span className="text-xs text-[#254e70]/60 ml-1">(Optional)</span>
            </label>
            <FieldDescription>
              Link to your professional website or practice page
            </FieldDescription>
            {isEditing ? (
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://yourpractice.com"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#9dacff]"
              />
            ) : (
              <p className="py-2 text-[#254e70]/80">
                {formData.website ? (
                  <button
                    onClick={() => handleWebsiteVisit(formData.website)}
                    className="text-[#9dacff] hover:underline text-left"
                  >
                    {formData.website}
                  </button>
                ) : (
                  'Not specified'
                )}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfileInformationForm; 